import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  useCreateKnowledgeBase,
  useDeleteKnowledgeBase,
  useKnowledgeBases,
} from '@/lib/api/knowledge';
import { Card } from '@/components/ui/Card';
import { Button, buttonClasses } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { QueryState } from '@/components/QueryState';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { useWorkspacePath } from '@/lib/workspacePath';
import type { KnowledgeBase } from '@/lib/api/types';

/** Knowledge base list: list, create, delete (spec 0014, AC-13). */
export function KnowledgeBasesListPage() {
  const bases = useKnowledgeBases();
  const createBase = useCreateKnowledgeBase();
  const deleteBase = useDeleteKnowledgeBase();
  const ws = useWorkspacePath();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createBase.mutate(
      { name: name.trim(), description: description.trim() || undefined },
      {
        onSuccess: () => {
          setName('');
          setDescription('');
        },
      },
    );
  }

  function handleDelete(base: KnowledgeBase) {
    const message =
      base.document_count > 0
        ? `Delete "${base.name}"? This will also delete its ${base.document_count} document(s) and unlink it from any agents.`
        : `Delete "${base.name}"? This will unlink it from any agents.`;
    if (!window.confirm(message)) return;
    deleteBase.mutate(base.id);
  }

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <Card
          title="Knowledge bases"
          description="Document collections your agents can be linked to search."
        >
          <QueryState
            isLoading={bases.isLoading}
            error={bases.error}
            isEmpty={!bases.data || bases.data.length === 0}
            emptyMessage="No knowledge bases yet. Create one to start uploading documents."
            loadingLabel="Loading knowledge bases"
            skeleton={<SkeletonRows rows={4} />}
          >
            <ul className="divide-y divide-gray-100" data-testid="knowledge-base-list">
              {bases.data?.map((base) => (
                <li
                  key={base.id}
                  className="flex items-center justify-between gap-4 py-3"
                  data-testid="knowledge-base-list-item"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{base.name}</p>
                    {base.description && (
                      <p className="truncate text-xs text-gray-500">{base.description}</p>
                    )}
                    <p className="text-xs text-gray-400">{base.document_count} document(s)</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Link
                      to={ws('knowledge', base.slug)}
                      data-testid="open-knowledge-base-link"
                      className={buttonClasses({ variant: 'secondary', size: 'sm' })}
                    >
                      Open
                    </Link>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(base)}
                      disabled={deleteBase.isPending}
                      data-testid="delete-knowledge-base-button"
                    >
                      Delete
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </QueryState>

          {deleteBase.error && (
            <p
              role="alert"
              className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {(deleteBase.error as Error).message}
            </p>
          )}
        </Card>
      </div>

      <div>
        <Card title="New knowledge base">
          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="knowledge-base-name-input"
            />
            <Textarea
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Describe what these documents cover."
              data-testid="knowledge-base-description-input"
            />

            {createBase.error && (
              <p
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {(createBase.error as Error).message}
              </p>
            )}

            <Button
              type="submit"
              disabled={!name.trim() || createBase.isPending}
              className="w-full"
              data-testid="create-knowledge-base-button"
            >
              {createBase.isPending ? 'Creating...' : 'Create knowledge base'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
